// src/components/padrekko/qc/PadRekkoDashboard.jsx
//
// PadRekko project-landing dashboard. Full-bleed surface shown (via MapPage)
// when the PadRekko QC tab is active, no case is loaded, and the viewer is not
// a restricted operator. Absorbs the case list: the operator-labeled roster IS
// the case list, promoted. A roster row opens its case via the existing
// loadCase() → the rail+map workspace renders unchanged.
//
// Data: GET /projects/:id/dashboard (header + stats + roster) + the existing
// /obligations summary for the strip. Case creation reuses QcPicker's
// NewCaseForm; the obligations report reuses ObligationsReport.

import { useEffect, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  ChevronRight, Plus, Clock, ArrowRight, Loader2, Layers, FolderOpen, ClipboardList,
} from "lucide-react";

import { useQcStore } from "../../../stores/useQcStore";
import { usePadStore } from "../../../stores/usePadStore";
import { useProjectStore } from "../../../stores/useProjectStore";
import { useEffectiveOrg } from "../../../hooks/useEffectiveOrg";
import { useAppNav } from "../../../hooks/useAppNav";
import { projectTypeLabel, projectTypeTooltip } from "../../../constants/projectTypes";
import { NewCaseForm } from "./QcPicker";
import ObligationsReport from "./ObligationsReport";

// Per-case review pill — derived from the case stage. customer_review is the
// customer (UL) review stage. Tones mirror QcPicker's stage palette.
const REVIEW_PILL = {
  dh_review:       { label: "Pending",        cls: "bg-slate-700/40 border-slate-600/50 text-fg-secondary", dot: "bg-slate-500" },
  customer_review: { label: "In review",      cls: "bg-sky-500/15   border-sky-500/40   text-sky-200",     dot: "bg-sky-300" },
  operator_review: { label: "In remediation", cls: "bg-purple-500/15 border-purple-500/40 text-purple-200", dot: "bg-purple-300" },
  complete:        { label: "Complete",       cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200", dot: "bg-emerald-300" },
};

function initials(name) {
  if (!name) return "—";
  const words = String(name).trim().split(/\s+/);
  return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? "")).toUpperCase() || "—";
}

function fmtDelivered(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function StatCard({ k, value, sub, tone }) {
  const toneCls =
    tone === "bad" ? "text-red-400" : tone === "warn" ? "text-amber-400" : tone === "good" ? "text-emerald-400" : "text-fg-primary";
  return (
    <div className="bg-slate-800/40 border border-slate-800 rounded-lg px-3.5 py-3">
      <div className="text-[11px] font-mono uppercase tracking-wider text-fg-secondary">{k}</div>
      <div className={`text-2xl font-semibold mt-1.5 tabular-nums ${toneCls}`}>
        {value}
        {sub != null && <span className="text-[15px] text-fg-secondary font-medium">{sub}</span>}
      </div>
    </div>
  );
}

export default function PadRekkoDashboard() {
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const activeProject   = useProjectStore((s) => s.activeProject);
  const setPadrekkoTab  = usePadStore((s) => s.setPadrekkoTab);

  const dash        = useQcStore((s) => s.projectDashboard);
  const dashError   = useQcStore((s) => s.projectDashboardError);
  const obligations = useQcStore((s) => s.obligations);
  const loadProjectDashboard = useQcStore((s) => s.loadProjectDashboard);

  const loadCase   = useQcStore((s) => s.loadCase);
  const createCase = useQcStore((s) => s.createCase);
  const loading    = useQcStore((s) => s.loading);
  const error      = useQcStore((s) => s.error);

  // Case creation is restricted to service-provider orgs (e.g. DH). Mirrors
  // QcPicker; the backend enforces the same gate in POST /cases.
  const isServiceProvider = useEffectiveOrg()?.is_service_provider === true;

  const [mode, setMode] = useState("home"); // "home" | "obligations"

  // newCaseSource: null | "kmz" | "selection" — which door opened NewCaseForm.
  //   "kmz"       → inline "Ingest New Case" (upload).
  //   "selection" → arrives from the case builder's "Continue to case details"
  //                 (?newcase=selection); pads come from useCaseBuilderStore.
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const [newCaseSource, setNewCaseSource] = useState(
    search?.newcase === "selection" ? "selection" : null
  );
  // Consume + strip the ?newcase param once so refresh/back can't re-trigger it.
  useEffect(() => {
    if (!search?.newcase) return;
    navigate({
      to: "/map/$tab",
      params: { tab: "padrekko" },
      search: (prev) => ({ ...prev, newcase: undefined }),
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Create New Case" deep-links into the Explore map's case-builder mode,
  // carrying a return target so "Continue to case details" lands back here in
  // NewCaseForm. "Ingest New Case" opens the inline KMZ form.
  const { openMap } = useAppNav();
  const openCaseBuilder = () =>
    openMap({ mapMode: "padrekko_builder", returnTo: "padrekko" });

  const hasProject = activeProjectId && activeProjectId !== "global";

  useEffect(() => {
    if (hasProject) loadProjectDashboard(activeProjectId);
  }, [hasProject, activeProjectId, loadProjectDashboard]);

  // ── sub-modes reuse existing components ──
  if (newCaseSource) {
    return (
      <Shell>
        <NewCaseForm
          source={newCaseSource}
          onCancel={() => setNewCaseSource(null)}
          loading={loading}
          error={error}
          createCase={createCase}
        />
      </Shell>
    );
  }
  if (mode === "obligations") {
    return (
      <Shell>
        <button
          onClick={() => setMode("home")}
          className="mb-3 text-[12px] text-fg-secondary hover:text-slate-200 transition flex items-center gap-1"
        >
          ← Back to dashboard
        </button>
        <ObligationsReport onClose={() => setMode("home")} />
      </Shell>
    );
  }

  // ── no project selected ──
  if (!hasProject) {
    return (
      <Shell>
        <EmptyHint
          icon={<FolderOpen size={20} />}
          title="Select a project"
          body="Pick a project from the picker above to see its PadRekko dashboard — stats, obligations, and the case roster by operator."
        />
      </Shell>
    );
  }

  if (!dash && !dashError) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2 py-24 text-fg-secondary">
          <Loader2 size={18} className="animate-spin" /> Loading dashboard…
        </div>
      </Shell>
    );
  }
  if (dashError && !dash) {
    return (
      <Shell>
        <EmptyHint
          icon={<Layers size={20} />}
          title="Couldn’t load the dashboard"
          body={dashError}
        />
      </Shell>
    );
  }

  const project = dash?.project ?? {};
  const stats   = dash?.stats ?? {};
  const roster  = dash?.roster ?? [];
  const delivered = fmtDelivered(project.delivered_at);

  // obligations strip — collapse the existing summary buckets
  const os = obligations?.summary;
  const overdue = os ? (os.overdue_1_30 ?? 0) + (os.overdue_31_60 ?? 0) + (os.overdue_60_plus ?? 0) : null;
  const upcoming = os?.upcoming ?? null;
  // `no_response` on the wire, "awaiting a date" on screen (15g8). The words
  // "no response" now name the CASE-level derivation — shared, deadline passed,
  // nobody answered — which is a worse fact than a finding with no committed
  // date. Two claims under one label on one page is the defect 098 and 099
  // avoided in the data; this avoids it in the copy. REM-305 tracks the key.
  const awaitingDate = os?.no_response ?? null;

  const actionedPct = stats.sites ? Math.round((stats.actioned / stats.sites) * 100) : 0;

  // Project selected, but no cases visible to this org. Show an honest empty
  // state (name + prompt) rather than a header of zeros, which reads as broken.
  // The project name comes from the picker selection (activeProject) since the
  // endpoint derives it from the first case, which doesn't exist here.
  if (roster.length === 0) {
    const name = project.name ?? activeProject?.name ?? "Project";
    return (
      <Shell>
        <div className="bg-[#102536] border border-slate-700 border-l-[3px] border-l-[#16f2d4] rounded-xl px-6 py-5 mb-4 flex items-start justify-between gap-6">
          <div className="min-w-0">
            <h1 className="text-[16px] font-semibold tracking-tight text-fg-primary">{name}</h1>
            <div className="text-[12px] text-fg-secondary mt-1">PadRekko</div>
          </div>
          <button
            onClick={() => setPadrekkoTab("volumetrics")}
            className="text-[12px] text-accent-action hover:brightness-110 transition flex items-center gap-1 shrink-0"
          >
            Volumetrics tools <ArrowRight size={12} />
          </button>
        </div>
        <div className="bg-slate-800/40 border border-slate-800 rounded-xl">
          <EmptyHint
            icon={<ClipboardList size={22} />}
            title="No cases yet"
            body={
              isServiceProvider
                ? "This project doesn’t have any cases yet. Create the first one to start a PadRekko reclamation review."
                : "This project has no cases assigned to your organization. Pick another project from the toolbar above."
            }
            action={
              isServiceProvider ? (
                <div className="mt-1 flex items-center gap-2">
                  <button
                    onClick={openCaseBuilder}
                    className="inline-flex items-center gap-1.5 text-[12px] font-medium text-bg bg-accent-action hover:brightness-110 transition rounded-md px-3 py-1.5"
                  >
                    <Plus size={14} /> Create New Case
                  </button>
                  <button
                    onClick={() => setNewCaseSource("kmz")}
                    className="inline-flex items-center gap-1.5 text-[12px] font-medium text-accent-action hover:brightness-110 transition border border-slate-700 rounded-md px-3 py-1.5"
                  >
                    <Plus size={14} /> Ingest New Case
                  </button>
                </div>
              ) : null
            }
          />
        </div>
      </Shell>
    );
  }

  return (
    <div className="h-full w-full flex flex-col bg-bg">
      {/* Sticky top — header, metrics, and obligations stay put; only the
          case roster scrolls (below). */}
      <div className="shrink-0 px-8 pt-5">
      {/* header band */}
      <div className="bg-[#102536] border border-slate-700 border-l-[3px] border-l-[#16f2d4] rounded-xl px-6 py-5 mb-4 flex gap-6 items-start">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-[16px] font-semibold tracking-tight text-fg-primary">
              {project.name ?? activeProject?.name ?? "Project"}
            </h1>
            {/* type badge — purpose / lifecycle phase (distinct from framework chips) */}
            <span title={projectTypeTooltip(project.type ?? activeProject?.type)}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-violet-400/30 bg-violet-400/10 text-violet-200">
              <FolderOpen size={10} className="opacity-70" />
              {projectTypeLabel(project.type ?? activeProject?.type)}
            </span>
          </div>
          <div className="text-[12px] text-fg-secondary mt-1">
            PadRekko{project.location ? ` · ${project.location}` : ""}
          </div>
          {project.frameworks?.length > 0 && (
            <div className="flex gap-1.5 mt-3 flex-wrap">
              {project.frameworks.map((fw) => (
                <span key={fw} className="text-[12px] bg-slate-700/40 border border-slate-700 text-slate-300 px-2.5 py-1 rounded-md">
                  {fw}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          {project.status && (
            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium bg-amber-500/15 text-amber-300 px-3 py-1.5 rounded-lg">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              {project.status}
            </span>
          )}
          <div className="text-[12.5px] text-fg-secondary">
            Delivered by <b className="text-fg-primary font-medium">{project.delivered_by}</b>
            {delivered ? ` · ${delivered}` : ""}
          </div>
          <button
            onClick={() => setPadrekkoTab("volumetrics")}
            className="text-[12px] text-accent-action hover:brightness-110 transition flex items-center gap-1"
          >
            Volumetrics tools <ArrowRight size={12} />
          </button>
        </div>
      </div>

      {/* stat cards */}
      <div className="grid grid-cols-7 gap-2.5 mb-3.5 max-[900px]:grid-cols-3">
        <StatCard k="Operators" value={stats.operators ?? 0} />
        <StatCard k="Sites" value={stats.sites ?? 0} />
        <StatCard k="Findings" value={stats.findings ?? 0} />
        <StatCard k="Non-compliant" value={stats.non_compliant ?? 0} tone="bad" />
        <StatCard k="Field-verify" value={stats.field_verify ?? 0} tone="warn" />
        <StatCard k="Compliant" value={stats.compliant ?? 0} tone="good" />
        <div className="bg-slate-800/40 border border-slate-800 rounded-lg px-3.5 py-3">
          <div className="text-[11px] font-mono uppercase tracking-wider text-fg-secondary">Actioned</div>
          <div className="text-2xl font-semibold mt-1.5 tabular-nums text-fg-primary">
            {stats.actioned ?? 0}
            <span className="text-[15px] text-fg-secondary font-medium">/{stats.sites ?? 0}</span>
          </div>
          <div className="h-[5px] rounded-full bg-slate-700/60 mt-2.5 overflow-hidden">
            <i className="block h-full bg-accent-info" style={{ width: `${actionedPct}%` }} />
          </div>
        </div>
      </div>

      {/* obligations strip */}
      <div className="flex items-center gap-4 bg-slate-800/40 border border-slate-800 rounded-lg px-4.5 py-3 mb-4 flex-wrap">
        <div className="font-medium flex items-center gap-2 text-fg-primary text-[13.5px]">
          <Clock size={14} className="text-fg-secondary" /> Remediation obligations
        </div>
        <div className="flex gap-2.5 flex-wrap">
          <ObChip tone="over" value={overdue} label="overdue" />
          <ObChip tone="up" value={upcoming} label="upcoming" />
          <ObChip tone="none" value={awaitingDate} label="awaiting a date" />
        </div>
        <button
          onClick={() => setMode("obligations")}
          className="ml-auto text-[13px] font-medium text-accent-action hover:brightness-110 transition flex items-center gap-1"
        >
          View obligations report <ArrowRight size={13} />
        </button>
      </div>
      </div>{/* end sticky top */}

      {/* Scroll region — only the case roster scrolls when it overflows. */}
      <div className="flex-1 min-h-0 px-8 pb-5 flex flex-col">
      {/* roster */}
      <div className="flex flex-col min-h-0 bg-slate-800/40 border border-slate-800 rounded-xl overflow-hidden">
        <div className="shrink-0 flex items-center justify-between px-[18px] py-3.5 border-b border-slate-800">
          <div className="flex items-baseline gap-2.5">
            <h2 className="text-[14px] font-semibold text-fg-primary">Cases, by operator</h2>
            <span className="text-[12px] text-fg-secondary">
              {stats.operators ?? 0} {stats.operators === 1 ? "operator" : "operators"} · {roster.length} {roster.length === 1 ? "case" : "cases"}
            </span>
          </div>
          {isServiceProvider && (
            <div className="flex items-center gap-3">
              <button
                onClick={openCaseBuilder}
                className="flex items-center gap-1.5 text-[12px] font-medium text-accent-action hover:brightness-110 transition"
              >
                <Plus size={14} /> Create New Case
              </button>
              <button
                onClick={() => setNewCaseSource("kmz")}
                className="flex items-center gap-1.5 text-[12px] font-medium text-fg-secondary hover:text-slate-200 transition"
              >
                <Plus size={14} /> Ingest New Case
              </button>
            </div>
          )}
        </div>

        {/* rows — the only scrolling part */}
        <div className="flex-1 min-h-0 overflow-y-auto">
        {roster.length === 0 ? (
          <div className="px-[18px] py-12 text-center text-[13px] text-fg-secondary">
            No cases in this project yet.
            {isServiceProvider && " Use “New case” to create one."}
          </div>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="text-left text-[10.5px] font-mono uppercase tracking-wider text-fg-secondary">
                <th className="px-[18px] py-2.5 font-medium border-b border-slate-800">Assigned Operator</th>
                <th className="px-[18px] py-2.5 font-medium border-b border-slate-800">Opened</th>
                <th className="px-[18px] py-2.5 font-medium border-b border-slate-800 text-right">Pads</th>
                <th className="px-[18px] py-2.5 font-medium border-b border-slate-800 text-right">Findings</th>
                <th className="px-[18px] py-2.5 font-medium border-b border-slate-800 text-right">NC</th>
                <th className="px-[18px] py-2.5 font-medium border-b border-slate-800">Review</th>
                <th className="px-[18px] py-2.5 font-medium border-b border-slate-800">Remediation</th>
                <th className="px-[18px] py-2.5 font-medium border-b border-slate-800" />
              </tr>
            </thead>
            <tbody>
              {roster.map((r) => {
                const pill = REVIEW_PILL[r.stage] ?? REVIEW_PILL.dh_review;
                return (
                  <tr
                    key={r.case_id}
                    onClick={() => loadCase(r.case_id)}
                    className="border-b border-slate-800 last:border-0 hover:bg-slate-700/20 cursor-pointer transition group"
                  >
                    <td className="px-[18px] py-4">
                      <div className="flex items-center gap-3">
                        <span className="w-[34px] h-[34px] rounded-lg bg-slate-700/50 text-slate-200 flex items-center justify-center text-[12px] font-semibold shrink-0">
                          {initials(r.operator?.name)}
                        </span>
                        <div className="min-w-0">
                          <div className="font-medium text-[12px] text-fg-primary truncate">
                            {r.operator?.name ?? r.name ?? "Unassigned operator"}
                          </div>
                          <div className="text-[12px] text-fg-secondary truncate">{r.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-[18px] py-4 text-[12px] text-fg-secondary whitespace-nowrap tabular-nums">{fmtDate(r.opened_at)}</td>
                    <td className="px-[18px] py-4 text-right tabular-nums font-medium text-fg-primary">{r.pads}</td>
                    <td className="px-[18px] py-4 text-right tabular-nums font-medium text-fg-primary">{r.findings}</td>
                    <td className="px-[18px] py-4 text-right tabular-nums font-semibold text-red-400">{r.nc}</td>
                    <td className="px-[18px] py-4">
                      <div className="flex items-center gap-2.5">
                        <span className={`inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-full border ${pill.cls}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${pill.dot}`} />
                          {pill.label}
                        </span>
                        {r.pads > 0 && (
                          <span className="text-[12px] text-fg-secondary tabular-nums">{r.actioned}/{r.pads}</span>
                        )}
                      </div>
                      {r.pads > 0 && (
                        <div className="h-1 rounded-full bg-slate-700/60 mt-1.5 max-w-[120px] overflow-hidden">
                          <div className="h-full bg-accent-info" style={{ width: `${Math.round((r.actioned / r.pads) * 100)}%` }} />
                        </div>
                      )}
                    </td>
                    <td className="px-[18px] py-4 text-[13px] font-medium text-fg-primary">
                      {r.outstanding_remediation} <span className="text-fg-secondary font-normal">outstanding</span>
                    </td>
                    <td className="px-[18px] py-4 text-right">
                      <ChevronRight size={18} className="text-slate-500 group-hover:text-accent-info transition inline" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        </div>{/* end rows scroll */}
      </div>

      <div className="mt-4 shrink-0 text-[12px] text-slate-500">
        Counties &amp; frameworks are derived from each pad’s geography. Each row opens its case.
      </div>
      </div>{/* end scroll region */}
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="h-full w-full overflow-y-auto bg-bg">
      {/* Full-bleed: fills the whole content area rather than a centered
          1080px column (the mockup was centered; product wants full width). */}
      <div className="w-full px-8 py-5">{children}</div>
    </div>
  );
}

function ObChip({ tone, value, label }) {
  const map = {
    over: "bg-red-500/[0.12] border-red-500/30 text-red-400",
    up: "bg-amber-500/[0.12] border-amber-500/30 text-amber-400",
    none: "bg-slate-700/40 border-slate-700 text-fg-primary",
  };
  const dot = { over: "bg-red-400", up: "bg-amber-400", none: "bg-slate-500" }[tone];
  return (
    <span className={`flex items-center gap-1.5 text-[13px] px-2.5 py-1 rounded-md border ${map[tone]}`}>
      <span className={`w-[7px] h-[7px] rounded-full ${dot}`} />
      <b className="font-semibold">{value ?? "—"}</b> {label}
    </span>
  );
}

function EmptyHint({ icon, title, body, action = null }) {
  return (
    <div className="flex flex-col items-center justify-center text-center gap-2 py-24 px-6">
      <div className="text-fg-secondary">{icon}</div>
      <div className="text-[15px] font-medium text-fg-primary">{title}</div>
      <div className="text-[13px] text-fg-secondary max-w-[420px] leading-relaxed">{body}</div>
      {action}
    </div>
  );
}
